import React from 'react';

import { Code, Globe, LockPassword, LinkSecurityLock, Lock, Building } from '@vimeo/bokeh-icons';
import { translate } from '@vimeo/i18n-runtime';

import { PrivacyOptionType, AllVideoPrivacyType, EventPrivacyType } from '../types';

export const NEW_DESCRIPTIONS: Partial<Record<AllVideoPrivacyType | EventPrivacyType, string>> = {
  unlisted: translate('Anyone with the link can view'),
  nobody: translate('Only you and people you invite can view'),
  team: translate('All team members can view'),
  password: translate('Anyone with the link and password can view'),
  disable: translate('Embeddable on any site, but hidden on Vimeo'),
  embed_only: translate('Embeddable on any site, but hidden on Vimeo'),
  anybody: translate('Anyone on the internet can find and view'),
};

export const newIcons = (
  iconSize: string,
): Partial<Record<AllVideoPrivacyType | EventPrivacyType, React.JSX.Element>> => ({
  embed_only: <Code boxSize={iconSize} />,
  disable: <Code boxSize={iconSize} />,
  anybody: <Globe boxSize={iconSize} />,
  nobody: <Lock boxSize={iconSize} />,
  password: <LockPassword boxSize={iconSize} />,
  unlisted: <LinkSecurityLock boxSize={iconSize} />,
  team: <Building boxSize={iconSize} />,
});

const getTeamPrivacyTitle = (teamName?: string, isWorkspace?: boolean) => {
  if (!teamName && isWorkspace) {
    return translate('Workspace only');
  }

  if (!teamName) {
    return translate('Company only');
  }

  return translate({ singular: '{TEAM_NAME} only', replacements: { TEAM_NAME: teamName } });
};

const getTeamPrivacyDescription = (teamName?: string, isWorkspace?: boolean) => {
  if (!teamName && isWorkspace) {
    return translate('All workspace members can view');
  }

  if (!teamName) {
    return translate('All company members can view');
  }
  return translate({
    singular: 'All {TEAM_NAME} members can view',
    replacements: { TEAM_NAME: teamName },
  });
};

export const getNewPrivacyCopy =
  (iconSize: string, teamName?: string, isWorkspace?: boolean) =>
  <T extends AllVideoPrivacyType | EventPrivacyType>(
    option: PrivacyOptionType<T>,
  ): PrivacyOptionType<T> => {
    return {
      ...option,
      title:
        option.privacy === 'team'
          ? getTeamPrivacyTitle(teamName, isWorkspace)
          : option.privacy === 'embed_only' || option.privacy === 'disable'
            ? translate('Embed only')
            : option.title,
      description:
        option.privacy === 'team'
          ? getTeamPrivacyDescription(teamName, isWorkspace)
          : (NEW_DESCRIPTIONS[option.privacy] ?? option.description),
      icon: newIcons(iconSize)[option.privacy] ?? option.icon,
    };
  };
